import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CalendarClock, Download, Loader2, Trash2 } from "lucide-react";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const STATUSES = ["new", "contacted", "trial_booked", "enrolled", "closed", "cancelled"];
const label = (s) => s.replace("_", " ");
const when = (iso) => (iso ? new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "");

const Reschedule = ({ e, headers, onDone }) => {
  const [slots, setSlots] = useState(null);
  useEffect(() => {
    const end = new Date(Date.now() + 30 * 86400000).toISOString();
    axios.get(`${API}/trial/slots`, { params: { start: new Date().toISOString(), end } }).then((r) => setSlots(r.data.slots));
  }, []);
  const pick = async (slot) => {
    try { onDone((await axios.put(`${API}/admin/trial/enquiries/${e.enquiry_id}`, { slot_start: slot }, { headers })).data); toast.success("Rescheduled - parent notified"); }
    catch (err) { toast.error(err.response?.data?.detail || "Couldn't reschedule"); }
  };
  if (!slots) return <Loader2 className="w-4 h-4 animate-spin" />;
  return (
    <select className="h-9 rounded-md border px-2 text-sm" defaultValue="" onChange={(ev) => ev.target.value && pick(ev.target.value)} data-testid={`enq-reschedule-${e.enquiry_id}`}>
      <option value="">Pick a new slot…</option>{slots.map((s) => <option key={s} value={s}>{when(s)}</option>)}
    </select>
  );
};

const Row = ({ e, headers, onChange, onDelete }) => {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState(e.notes || "");
  const [link, setLink] = useState(e.meeting_link || "");
  const update = async (body, msg) => {
    try { onChange((await axios.put(`${API}/admin/trial/enquiries/${e.enquiry_id}`, body, { headers })).data); if (msg) toast.success(msg); }
    catch (err) { toast.error(err.response?.data?.detail || "Update failed"); }
  };
  return (
    <div className="px-4 py-3" data-testid={`enq-row-${e.enquiry_id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <button className="flex-1 min-w-[200px] text-left" onClick={() => setOpen(!open)} data-testid={`enq-toggle-${e.enquiry_id}`}>
          <p className="font-semibold">{e.parent_name} {e.child_name && <span className="font-normal text-slate-500">· {e.child_name} ({e.child_age || "?"})</span>}</p>
          <p className="text-xs text-slate-500">{e.type === "trial" ? <span className="inline-flex items-center gap-1 text-[#2563eb] font-semibold"><CalendarClock className="w-3 h-3" />{when(e.slot_start)}</span> : "Enquiry"} · {e.programme || "General"} · received {when(e.created_at)}</p>
        </button>
        <select className="h-8 rounded-md border px-2 text-xs capitalize" value={e.status} onChange={(ev) => (ev.target.value !== "cancelled" || window.confirm("Cancel this trial? The parent will be emailed.")) && update({ status: ev.target.value }, "Status updated")} data-testid={`enq-status-${e.enquiry_id}`}>
          {STATUSES.map((s) => <option key={s} value={s}>{label(s)}</option>)}
        </select>
      </div>
      {open && (
        <div className="mt-3 grid md:grid-cols-2 gap-3 text-sm">
          <div className="space-y-1 text-slate-600">
            <p><b>Email:</b> <a className="text-[#2563eb]" href={`mailto:${e.email}`}>{e.email}</a></p>
            <p><b>Phone:</b> {e.phone || "-"} · <b>Country:</b> {e.country || "-"}</p>
            {e.visitor_timezone && <p><b>Parent time zone:</b> {e.visitor_timezone}</p>}
            {e.message && <p className="whitespace-pre-line"><b>Message:</b> {e.message}</p>}
            {e.gcal_event_id && <p className="text-emerald-700">Synced to Google Calendar</p>}
          </div>
          <div className="space-y-2">
            {e.type === "trial" && e.status !== "cancelled" && (
              <>
                <div className="flex gap-2"><Input value={link} onChange={(ev) => setLink(ev.target.value)} placeholder="https:// meeting link" data-testid={`enq-link-${e.enquiry_id}`} />
                  <Button size="sm" variant="outline" onClick={() => update({ meeting_link: link }, "Link saved - parent emailed")} data-testid={`enq-link-save-${e.enquiry_id}`}>Send link</Button></div>
                <Reschedule e={e} headers={headers} onDone={onChange} />
              </>
            )}
            <Textarea rows={2} value={notes} onChange={(ev) => setNotes(ev.target.value)} placeholder="Private notes" data-testid={`enq-notes-${e.enquiry_id}`} />
            <div className="flex gap-2">
              <Button size="sm" variant="secondary" onClick={() => update({ notes }, "Notes saved")} data-testid={`enq-notes-save-${e.enquiry_id}`}>Save notes</Button>
              <Button size="sm" variant="ghost" className="text-red-600 ml-auto" onClick={() => window.confirm("Delete this enquiry permanently?") && onDelete()} data-testid={`enq-delete-${e.enquiry_id}`}><Trash2 className="w-4 h-4 mr-1" />Delete</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const EnquiriesManager = () => {
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [data, setData] = useState({ items: [], counts: {} });
  const [status, setStatus] = useState("");
  const load = useCallback(() => axios.get(`${API}/admin/trial/enquiries`, { headers, params: { status } }).then((r) => setData(r.data)), [headers, status]);
  useEffect(() => { load(); }, [load]);
  const exportCsv = async () => {
    const r = await axios.get(`${API}/admin/trial/enquiries.csv`, { headers, responseType: "blob" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = "enquiries.csv"; a.click();
  };
  return (
    <div className="space-y-3" data-testid="enquiries-manager">
      <div className="flex flex-wrap gap-2 items-center">
        {["", ...STATUSES].map((s) => (
          <button key={s} onClick={() => setStatus(s)} className={`px-3 h-8 rounded-full text-xs font-semibold capitalize border ${status === s ? "bg-[#2563eb] text-white border-transparent" : "bg-white"}`} data-testid={`enq-filter-${s || "all"}`}>
            {s ? `${label(s)} (${data.counts[s] || 0})` : "All"}
          </button>
        ))}
        <Button size="sm" variant="outline" className="ml-auto" onClick={exportCsv} data-testid="enq-export"><Download className="w-4 h-4 mr-1" />Export CSV</Button>
      </div>
      <div className="rounded-xl border bg-white divide-y">
        {!data.items.length && <p className="p-6 text-center text-sm text-muted-foreground">No enquiries yet.</p>}
        {data.items.map((e) => <Row key={e.enquiry_id} e={e} headers={headers} onChange={() => load()}
          onDelete={async () => { await axios.delete(`${API}/admin/trial/enquiries/${e.enquiry_id}`, { headers }); load(); }} />)}
      </div>
    </div>
  );
};
